import * as React from 'react';
import { useLoaderData, LoaderFunctionArgs } from "react-router-dom";
import { Patient, Gender, TrackableMeasure, TrackableMeasureTypes, Visit, VisitStatus, PatientStatus, 
    Client, DocumentRef, DocumentStatus, 
} from '../../models/core';
import { AuthData } from '../../models/ziphy';
import { API } from '../../utils/Api';
import { PublicAPI } from '../../utils/PublicAPI';
import { useLoading } from '../../hooks/useLoading';
import { AppBar, BottomNavigation, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, 
    DialogTitle, Divider, Grid, Link, Paper, Stack, TextField, Toolbar, Typography } from '@mui/material';
import { GridFormItem } from '../../components/GridForm';
import { AutoCloseEnumField, EnumField } from '../../components/fields';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { ComplianceList, Genders, Languages, MeasureEntryList, States, VisitReasons } from '../../utils/enums';
import PhoneFaxEmailView from '../../components/patients/2_0/PhoneFaxEmailView';
import CollapsibleSection from '../../components/CollapsibleSection'

import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import TextTable from '../../components/TextTable';
import FileUploadPreview, { PreviewFile } from '../../components/patients/2_0/documents/FileUploadPreview';
import InsuranceList from '../../components/patients/2_0/insurance/InsuranceList';
import { useQuery } from '@tanstack/react-query';
import { Insurance } from '../../models/insurance';
import GridFormTextField from '../../components/patients/2_0/GridFormTextField';
import ReferralSubmitDialog from '../../components/dialogs/ReferralSubmitDialog';
import SimpleDialog from '../../components/dialogs/SimpleDialog';
import useSnackbar from '../../hooks/useSnackbar';
import AlertSnack from '../../components/ErrorSnack';


export const loader = async ({ params, request }: LoaderFunctionArgs): Promise<[string,boolean]> => {
    let clientId = params.clientId;
    if (!clientId) {
        const match = request.url.match(/referals\/(?<clientId>\w+)\//);
        clientId = match && match.groups && match.groups['clientId'] || '';
        if (!clientId) {
            throw Error('Missing clientId param');
        }
    }
    const publicAPI = request.url.toLocaleLowerCase().includes('/p/referals/');

    //  Check if we have access to the private APIS
    if(!API.token || API.token == '') {
        const authItem = window.localStorage.getItem('auth');
        const auth = authItem ? (JSON.parse(authItem) as AuthData) : null;
        API.token = auth?.accessToken?.value || '';
    }
    return [clientId,publicAPI];
}

const parseAge = (date:DateTime|null) => {
    if(date === null) { return ''; }
    let ageNow = 0;
    try {
        const today = DateTime.now();
        ageNow = today.year - date.year;
        const month = today.month - date.month;
        if (month < 0 || (month === 0 && today < date)) {
            ageNow--;
        }
    } catch (error) { console.warn(error); }
    return Number.isNaN(ageNow) ? '' : ageNow.toString();
}

const checkErrorString = (_key:string, required:boolean, value?:string) => {
    if(!required) { return false; }
    return !value || value.length == 0;
};

const verifyCanAddConditionRow = (nr: TrackableMeasure, cbk:()=>void) => {
    if(!nr.name || nr.name == '') {
        return [{ key:'measure', label:'Measure must be set' }];
    }
    cbk();
    return undefined;
};

function useClientQuery(clientId:string, retireveClient?:(clientId: string) => Promise<Client>) {
    return useQuery({
        queryKey: ['pubic.client.list', clientId],
        queryFn: async () => (
            retireveClient
                ? await retireveClient(clientId)
                : await API.getClient(clientId)
        ),
        select: (data) => data,
        staleTime:3600_000
    });
}

const submitPatientPrivate = async (clientId:string, patient:Patient, insurances:Insurance[], visit:Visit) => {
    const reqCheckPatient = await API.getPatientsQuery([['clientId', clientId], ['refId', patient?.refId]]);
    if(reqCheckPatient && reqCheckPatient.count > 0) {
        throw new Error('Duplicate')
    }

    const newPatientReq = await API.createPatient(patient);
    if(!newPatientReq || !newPatientReq.acknowledged || !newPatientReq.insertedId || newPatientReq.insertedId == '') {
        throw new Error('Failed to Create Patient')
    }
    
    const patientId = newPatientReq.insertedId;
    if(insurances && insurances.length != 0) {
        const newInsutanceList = insurances.map((i) => ({...i, clientId:clientId, patientId:patientId}));
        for(const insuracne of newInsutanceList) {
            const ack = await API.createInsurance(clientId, patientId, insuracne);
            if(!ack || !ack.acknowledged) {
                console.error('Failed to create insurance');
            }
        }
    }
    const reqVisit = await API.createVisit({ ...visit, patientId:patientId });
    if(!reqVisit || !reqVisit.acknowledged) {
        console.error('Failed to create visit');
    }
}

interface Signer {
    firstName:string;
    lastName:string;
    contact:string;
    contactType:'email'|'phone';
}

interface DocContainer {
    src:string;
    file:File;
    doc:DocumentRef;
}

async function calculateSha256Base64(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    let binary = '';
    hashArray.forEach(byte => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
}

const submitPatientPublic = async (client:Client, patient:Patient, insurances:Insurance[], 
    visit:Visit, measures:TrackableMeasure[], signer:Signer, documents:DocContainer[], 
    onError:(error:string) => void) => {
    
    const clientIdStr = client._id.toString();
    const upInsurance = insurances.map((i) => ({...i, clientId:clientIdStr }));
    const reqAck = await PublicAPI.createPatient(clientIdStr, patient, visit, upInsurance, measures, signer);
    if(!reqAck || reqAck.acknowledged == false) {
        throw Error('Invalid');
    }

    if(!documents || documents.length == 0) {
        return;
    }
    
    for(const doc of documents) {
        
        const checkSum = await calculateSha256Base64(doc.file);
        const docRequest = await PublicAPI.createDocument(client, reqAck.insertedId, checkSum, doc.doc, signer);
        try {

            if(!docRequest || docRequest.error || !docRequest.acknowledged || !docRequest.data) {
                console.error('Failed to upload $s with error $s', doc.doc.name, docRequest.error || 'unknown');
                continue;
            }

            if(docRequest.data?.url && docRequest.data?.url?.trim() !== '') {
                const uploadRes = await fetch(docRequest.data?.url , {
                    method: 'PUT',
                    headers: {
                        'Content-Type': doc.doc.type,
                        'x-amz-checksum-sha256': checkSum,
                    },
                    body: doc.file
                });

                if(!uploadRes?.ok) {
                    const error = await uploadRes.text();
                    console.error(error);
                    await API.abortDocument(patient.clientId, patient._id, docRequest.insertedId)
                    onError(`Failed to create document: ${doc.doc.name}`)
                    continue;
                }
            }
            //await PublicAPI.abortDocument(clientId, reqAck.insertedId, docRequest.insertedId);
            await PublicAPI.finalizeDocument(clientIdStr, reqAck.insertedId, docRequest.insertedId, signer);            

        } catch (error) {
            onError(`Failed to create document: ${doc.doc.name}`);
            console.error(error);
            try { await API.abortDocument(patient.clientId, patient._id, docRequest.insertedId) } catch(err) { console.error(error) }
        }
    }
}

const DefaultLanauge = 'English';
const DemographicsRequire = ['firstName', 'lastName', 'dob'];
const ContactsRequire = ['phone', 'address', 'city', 'state', 'zip'];
const ClientsRequire = ['refId'];
const ProvidersRequire:string[] = [];
const CaseManagerRequire:string[] = [];

const ReferalsPage = (): JSX.Element => {
    const snackBar = useSnackbar();
    const [clientId, publicAPI] = useLoaderData() as [string, boolean];
    const clientQuery = useClientQuery(clientId, publicAPI ? PublicAPI.getClient : undefined);

    const loading = useLoading();

    const [demographicsPatient, setDemographicsPatient] = React.useState<Partial<Patient>>(
        {status:PatientStatus.ACTIVE, language:DefaultLanauge, gender:Gender.UNKNOWN});
    const [demographicErrors] = React.useState<Record<string, boolean>>(
        DemographicsRequire.reduce((acc, key) => { acc[key] = true; return acc; }, {} as Record<string, boolean>)
    );

    const [contactsPatient, setContactsPatient] = React.useState<Partial<Patient>>({});
    const [contactErrors] = React.useState<Record<string, boolean>>(
        ContactsRequire.reduce((acc, key) => { acc[key] = true; return acc; }, {} as Record<string, boolean>)
    );

    const [clientsPatient, setClientPatient] = React.useState<Partial<Patient>>({});
    const [clientErrors] = React.useState<Record<string, boolean>>(
        ClientsRequire.reduce((acc, key) => { acc[key] = true; return acc; }, {} as Record<string, boolean>)
    );

    const [providersPatient, setProviderPatient] = React.useState<Partial<Patient>>({});
    const [providerErrors] = React.useState<Record<string, boolean>>(
        ProvidersRequire.reduce((acc, key) => { acc[key] = true; return acc; }, {} as Record<string, boolean>)
    );

    const [caseManagerPatient, setCaseManagerPatient] = React.useState<Partial<Patient>>({});
    const [caseManagerErrors] = React.useState<Record<string, boolean>>(
        CaseManagerRequire.reduce((acc, key) => { acc[key] = true; return acc; }, {} as Record<string, boolean>)
    );

    const [suspectConditionList, setSuspectConditionList] = React.useState<TrackableMeasure[]>([]);
    const [diagnosedConditionList, setDiagnosedConditionList] = React.useState<TrackableMeasure[]>([]);
    const [chronicConditionList, setChronicConditionList] = React.useState<TrackableMeasure[]>([]);
    const [measureList, setMeasureList] = React.useState<TrackableMeasure[]>([]);

    const [submitterFirstName, setSubmitterFirstName] = React.useState<string>();
    const [submitterLastName, setSubmitterLastName] = React.useState<string>();
    const [submitterEmail, setSubmitterEmail] = React.useState<string>();
    
    const [visitReasons, setVisitReasons] = React.useState<string[]>([]);
    const [dischargePlace, setDischargePlace] = React.useState<string>();
    const [dischargeDate, setDischargeDate] = React.useState<string>();
    const [dischargeNotes, setDischargeNotes] = React.useState<string>();
    const [insuranceList, setInsuranceList] = React.useState<Insurance[]>([]);

    const [referalSubmitOpen, setReferalSubmitOpen] = React.useState<boolean>(false);
    const [diagSuccess, setDiagSuccess] = React.useState<boolean>(false);
    const [diagDuplicate, setDiagDuplicate] = React.useState<boolean>(false);
    const [diagFailed, setDiagFailed] = React.useState<boolean>(false);
    const [confirmDiag, setConfirmDiag] = React.useState<boolean>(false);
    const [files, setFiles] = React.useState<PreviewFile[]>([]);

    React.useEffect(() => {loading.hideLoading();}, [])

    const demographicsComponents = React.useMemo<JSX.Element>(() => {
        const patientDOB = DateTime.fromISO(demographicsPatient.dob || '');
        const maxDate = DateTime.now().minus({year:3});
        function setField(pt:Partial<Patient>, field:string, error:boolean) {
            setDemographicsPatient(prev => ({ ...prev, ...pt}))
            demographicErrors[field] = error;
        }
        return (<GridFormItem xs={12}>
            <Grid container spacing={1.5} paddingBottom={'5px'}>
                <GridFormTextField size={6} name='firstName' label='First Name' value={demographicsPatient.firstName}
                    required={true} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                <GridFormTextField size={6} name='lastName' label='Last Name' value={demographicsPatient.lastName}
                    required={true} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>

                <GridFormItem xs={3}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker label='Date of Birth' inputFormat='MM/dd/yyyy'
                            value={ patientDOB }
                            maxDate={maxDate}
                            renderInput={
                                (params) => <TextField name='dob' required {...params} 
                                error={checkErrorString('dob', true, demographicsPatient.dob)}
                                variant='standard' helperText='Format: MM/DD/YYYY' />
                            }
                            onChange={(e) => {
                                const dateOfBirth = e?.toISODate()||undefined;
                                setField({dob:dateOfBirth}, 'dob', checkErrorString('dob', true, dateOfBirth))}
                            }
                        />
                    </LocalizationProvider>
                </GridFormItem>
                <GridFormItem xs={1}><Box sx={{ height: '1em' }} /></GridFormItem>
                <GridFormItem xs={1}>
                    <TextField name='age' label='Age' value={parseAge(patientDOB)} variant='standard'/>
                </GridFormItem>
                <GridFormItem xs={1}><Box sx={{ height: '1em' }} /></GridFormItem>
                <GridFormItem xs={2}>
                    <EnumField name='gender' label='Sex Assigned Birth' value={demographicsPatient.gender||Gender.UNKNOWN}
                        onChange={(_,v) => setField({gender:v||Gender.UNKNOWN}, 'gender', false) } options={Genders} required/>
                </GridFormItem>
                <GridFormItem xs={1} ><Box sx={{ height: '1em' }} /></GridFormItem>
                <GridFormItem xs={3}>
                    <EnumField name='language' label='Language' value={ demographicsPatient.language||DefaultLanauge }
                        onChange={(_,v) => setField({language:v||DefaultLanauge}, 'language', false) } options={Languages} required/>
                </GridFormItem>
            </Grid>
        </GridFormItem>)
    },[demographicsPatient, demographicErrors]);

    const contactComponents = React.useMemo<JSX.Element>(() => {
        function setField(pt:Partial<Patient>, field:string, error:boolean) {
            setContactsPatient(prev => ({ ...prev, ...pt}))
            contactErrors[field] = error;
        }
        const hasErrors = Object.values(contactErrors)?.find(e => e) || false;

        return (
            <GridFormItem xs={12}>
            <Box sx={{border:1, borderRadius:1, padding:'1%', borderColor:hasErrors ? 'red' : undefined}}>
            <CollapsibleSection header={
                <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                    Contacts Information
                </Typography>
            }>
                <Grid container spacing={1.5}>
                    <GridFormItem xs={12}><Divider/></GridFormItem>

                    <GridFormItem xs={12}>
                        <Typography variant='h6' component='div' sx={{ display: 'flex', justifyContent: 'start' }}>Patient</Typography>
                        <Divider/>
                    </GridFormItem>
                    <GridFormItem xs={0.40}><PhoneIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={3.60} name='phone' label='Phone' value={contactsPatient.phone}
                        required={true} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={0.40}><PhoneIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={3.60} name='phone2' label='Alt Phone' value={contactsPatient.phone2}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={0.40}><EmailIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={3.60} name='email' label='Email' value={contactsPatient.email}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    
                    <GridFormItem xs={12}><Box sx={{ height: '0.25em' }} /></GridFormItem>

                    <GridFormTextField size={6} name='address' label='Address' value={contactsPatient.address}
                        required={true} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormTextField size={6} name='address2' label='Address 2' value={contactsPatient.address2}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    
                    <GridFormTextField size={4} name='city' label='City' value={contactsPatient.city}
                        required={true} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={2}>
                        <EnumField name='state' label='State' value={contactsPatient.state||''}
                            onChange={(_,v) => setField({state:v||undefined}, 'state', false) }
                            error={checkErrorString('state', true, contactsPatient.state)}
                            options={States.map(e=> [e.code, e.name])} required/>
                    </GridFormItem>

                    <GridFormTextField size={2} name='zip' label='ZIP' value={contactsPatient.zip}
                        inputProps={{ maxLength: 10 }}
                        required={true} checkError={(_name: string, _required: boolean, value: string | undefined) => {
                            if (value && /^\d{5}(-\d{4})?$/.test(value)) {
                                return false;
                            }
                            return _required;
                        }}
                        helperText={'Format: 12345 or 12345-6789'}
                        onChange={ (v,f,e) => setField({[f]:v},f,e) }
                    />

                    <GridFormTextField size={4} name='county' label='County' value={contactsPatient.county}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                        
                    <GridFormItem xs={12}><Box sx={{ height: '1em' }} /></GridFormItem>

                    <GridFormItem xs={12}>
                        <Typography variant='h6' component='div' sx={{ display: 'flex', justifyContent: 'start' }}>Guardian</Typography>
                        <Divider/>
                    </GridFormItem>
                    <GridFormTextField size={4} name='guardianName' label='Name' value={contactsPatient.guardianName}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={0.40}><PhoneIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={3.60} name='guardianPhone' label='Phone' value={contactsPatient.guardianPhone}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={0.40}><EmailIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={3.60} name='guardianEmail' label='Email' value={contactsPatient.guardianEmail}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>

                    <GridFormItem xs={12}><Box sx={{ height: '1em' }} /></GridFormItem>

                    <GridFormItem xs={12}>
                        <Typography variant='h6' component='div' sx={{ display: 'flex', justifyContent: 'start' }}>Emergency Contact</Typography>
                        <Divider/>
                    </GridFormItem>
                    <GridFormTextField size={6} name='ecFirstName' label='First Name' value={contactsPatient.ecFirstName}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormTextField size={6} name='ecLastName' label='Last Name' value={contactsPatient.ecLastName}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={0.40}><PhoneIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={5.60} name='ecPhone' label='Phone' value={contactsPatient.ecPhone}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={0.40}><EmailIcon sx={{marginTop:'16px'}}/></GridFormItem>
                    <GridFormTextField size={5.60} name='ecEmail' label='Email' value={contactsPatient.ecEmail}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>

                    <GridFormItem xs={12}><Box sx={{ height: '1em' }} /></GridFormItem>
                </Grid>
            </CollapsibleSection>
            </Box>
            </GridFormItem>
        )
    },[contactsPatient, contactErrors]);


    const clientComponents = React.useMemo<JSX.Element>(() => {
        function setField(pt:Partial<Patient>, field:string, error:boolean) {
            setClientPatient(prev => ({ ...prev, ...pt}))
            clientErrors[field] = error;
        }
        const hasErrors = Object.values(clientErrors)?.find(e => e) || false;

        return (
            <GridFormItem xs={12}>
                <Box sx={{border:1, borderRadius:1, padding:'1%', borderColor:hasErrors ? 'red' : undefined}}>
                <CollapsibleSection header={
                    <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                        Client
                    </Typography>
                }>
                <Grid container spacing={1.5}>
                    <GridFormItem xs={12}><Divider/></GridFormItem>

                    <GridFormTextField size={6} name='refId' label='Client ID' value={clientsPatient.refId}
                        required={true} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>

                    <GridFormTextField size={3} name='risk' label='Risk' value={clientsPatient.risk?.toString()}
                        required={false} checkError={checkErrorString} type='number'
                        onChange={ (v,f,e) => setField({risk:Number.parseFloat(v || '0')}, f, e) }/>

                    <GridFormTextField size={3} name='riskCategory' label='Risk Category' value={clientsPatient.riskCategory}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>

                </Grid>
            </CollapsibleSection>
            </Box>
            </GridFormItem>
        )
    },[clientsPatient, clientErrors]);

    const providerComponents = React.useMemo<JSX.Element>(() => {
        function setField(pt:Partial<Patient>, field:string, error:boolean) {
            setProviderPatient(prev => ({ ...prev, ...pt}))
            providerErrors[field] = error;
        }
        const hasErrors = Object.values(providerErrors)?.find(e => e) || false;

        return (<GridFormItem xs={12}>
            <Box sx={{border:1, borderRadius:1, padding:'1%', borderColor:hasErrors ? 'red' : undefined}}>
                <CollapsibleSection header={
                    <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                        Provider
                    </Typography>
                }>
                <Grid container spacing={1.5}>
                    <GridFormItem xs={12}><Divider/></GridFormItem>
                    <GridFormTextField size={6} name='pcpName' label='Provider Name' value={providersPatient.pcpName}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={2}/>
                    <GridFormTextField size={4} name='pcpNPI' label='Provider NPI' value={providersPatient.pcpNPI}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>

                    <GridFormItem xs={12}>
                        <PhoneFaxEmailView phone={providersPatient.pcpPhone} phoneLabel='Provider Phone'
                            fax={providersPatient.pcpFax} faxLabel='Provider Fax'
                            email={providersPatient.pcpEmail} emailLabel='Provider Email'
                            setField={(f, v) => {
                                switch(f) {
                                    case 'phone': setField({pcpPhone:v},'pcpPhone',false); break;
                                    case 'fax': setField({pcpFax:v},'pcpFax',false); break;
                                    case 'email': setField({pcpEmail:v},'pcpEmail',false); break;
                                }
                            }}
                        />
                    </GridFormItem>

                    <GridFormTextField size={6} name='practiceName' label='Practice Name' value={providersPatient.practiceName}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormTextField size={6} name='groupName' label='Group Name' value={providersPatient.groupName}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                </Grid>
            </CollapsibleSection>
            </Box>
            </GridFormItem>
        )
    }, [providerErrors, providersPatient]);

    const caseManagerComponents = React.useMemo<JSX.Element>(() => {
        function setField(pt:Partial<Patient>, field:string, error:boolean) {
            setCaseManagerPatient(prev => ({ ...prev, ...pt}))
            caseManagerErrors[field] = error;
        }
        const hasErrors = Object.values(caseManagerErrors)?.find(e => e) || false;

        return (<GridFormItem xs={12}>
            <Box sx={{border:1, borderRadius:1, padding:'1%', borderColor:hasErrors ? 'red' : undefined}}>
                <CollapsibleSection header={
                    <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                        Case Manager
                    </Typography>
                }>
                <Grid container spacing={1.5}>
                    <GridFormItem xs={12}><Divider/></GridFormItem>
                    
                    <GridFormTextField size={6} name='caseManager' label='Case Manager' value={caseManagerPatient.caseManager}
                        required={false} checkError={checkErrorString} onChange={ (v,f,e) => setField({[f]:v},f,e) }/>
                    <GridFormItem xs={6}/>

                    <GridFormItem xs={12}>
                        <PhoneFaxEmailView phone={caseManagerPatient.caseManagerPhone} phoneLabel='Case Manager Phone'
                            fax={caseManagerPatient.caseManagerFax} faxLabel='Case Manager Fax'
                            email={caseManagerPatient.caseManagerEmail} emailLabel='Case Manager Email'
                            setField={(f, v) => {
                                switch(f) {
                                    case 'phone': setField({caseManagerPhone:v},'caseManagerPhone',false); break;
                                    case 'fax': setField({caseManagerFax:v},'caseManagerFax',false); break;
                                    case 'email': setField({caseManagerEmail:v},'caseManagerEmail',false); break;
                                }
                            }}
                        />
                    </GridFormItem>
                </Grid>
                </CollapsibleSection>
            </Box>
            </GridFormItem>
        )
    },[caseManagerPatient, caseManagerErrors]);


    const visitReasonComponents = React.useMemo<JSX.Element>(() => {
        const rowLines = (dischargeNotes || '').split('\n').length || 1;
        const noteRows = rowLines <= 6 ? rowLines : 6;
        return (<GridFormItem xs={12}>
            <Box sx={{border:1, borderRadius:1, padding:'1%'}}>
                <CollapsibleSection header={
                    <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                        Visit
                    </Typography>
                }>
                <Grid container spacing={1.5}>
                    <GridFormItem xs={12}><Divider/></GridFormItem>
                    <GridFormItem xs={6}>
                        <AutoCloseEnumField name='visitReasons' multiple={true} value={visitReasons||[]}
                            options={VisitReasons.sort((a,b) => a.name.localeCompare(b.name)).map(e=> [e.code, e.name])} label='Reason'
                            onChange={(_: string, newValue: string[] | null) => {
                                setVisitReasons(newValue||[]);
                            }}
                        />
                    </GridFormItem>
                    <GridFormItem xs={6}/>

                    <GridFormTextField size={10} name='dischargePlace' label='Discharge Place' value={dischargePlace}
                        required={false} checkError={checkErrorString} onChange={ (v) => setDischargePlace(v)}/>

                    <GridFormItem xs={2}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DatePicker label='Discharge Date'
                                value={ DateTime.fromISO(dischargeDate || '') } inputFormat='MM/dd/yyyy'
                                renderInput={
                                    (params) => {
                                        const isRequired = (!!visitReasons && visitReasons.length !== 0
                                            && visitReasons.includes('Transition of Care')
                                        );
                                        const hasError = isRequired && !dischargeDate;

                                        return <TextField 
                                            {...params}
                                            name='dcDate'
                                            variant='standard'
                                            required={isRequired}
                                            error={hasError}
                                            helperText={'Format: MM/DD/YYYY'}
                                        />
                                    }
                                }
                                onChange={(e) => {
                                    const dd = e?.toISODate()||undefined;
                                    setDischargeDate(dd);
                                }}
                            />
                        </LocalizationProvider>
                    </GridFormItem>

                    <Grid item xs={12}>
                        <TextField name='dischargeNote' label='Discharge Notes' value={dischargeNotes || ''}
                            onChange={(ev) => { setDischargeNotes(ev.target.value) } }
                            variant='standard' fullWidth multiline rows={noteRows}
                        />
                    </Grid>
                </Grid>
                </CollapsibleSection>
            </Box>
            </GridFormItem>
        )
    },[visitReasons, dischargePlace, dischargeDate, dischargeNotes]);

    const measureListKeys = React.useMemo(() => {
        const sortedList = MeasureEntryList.sort((a, b) => a.name.localeCompare(b.name));
        return sortedList?.map((it) => { return { key:it.name, value:it.code } });
    },[]);

    const complianceListKeys = React.useMemo(() => {
        return ComplianceList?.map((it) => { return { key:it.name, value:it.code } });
    },[]);

    const createConditionsComponent = React.useCallback((
        setList:(value:React.SetStateAction<TrackableMeasure[]>) => void,
        verifyRow:(nr: TrackableMeasure, cbk: () => void) => {key: string; label: string;}[] | undefined,
        list:TrackableMeasure[],
        title:string,
        addRowHeader:string,
        trackableType:TrackableMeasureTypes,
        columns:{ key: keyof TrackableMeasure; label: string, default?:string, visible?:boolean, entries?:{key:string,value?:string}[], type?:'text'|'bool'|'enum' }[]
    ) => {
        const handleAddRow = (newRow: TrackableMeasure) => {
            setList([...list, {...newRow, id:Date.now().toString(), type:trackableType, created:Date.now().toString() }]);
        };
        const handleDeleteRow = (newRow: TrackableMeasure) => {
            setList([...list.filter(el => el.id != newRow.id)]);
        };
        const handleEditRow = (newRow: TrackableMeasure) => {
            const index = list.findIndex(el => el.id == newRow.id);
            if(index !== -1) {
                setList(prevArray => {
                    const newArray = [...prevArray];
                    newArray[index] = { ...newRow };
                    return newArray;
                });
            }
        };

        return (
            <GridFormItem xs={12}>
                <Box sx={{padding:'0.25%'}}>
                <CollapsibleSection header={
                    <Typography variant='h6' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                        {title}
                    </Typography>
                } defaultOpen={false}>
            <GridFormItem xs={12}>
                <TextTable
                    addRowHeader={addRowHeader}
                    rows={list}
                    columns={columns}
                    onAddRow={handleAddRow}
                    canAddRow={verifyRow}
                    onDeleteRow={handleDeleteRow}
                    onUpdateRow={handleEditRow}
                />
            </GridFormItem>
            </CollapsibleSection>
            </Box>
            </GridFormItem>)
    },[]);

    const suspectConditionComponents = React.useMemo<JSX.Element>(() => {
        return createConditionsComponent(setSuspectConditionList, verifyCanAddConditionRow, suspectConditionList,
            'Suspect Conditions', 'Enter the suspect condition and ICD code:', TrackableMeasureTypes.SUSPECT,
            [ { key: 'name', label: 'Name' }, { key: 'value', label: 'ICD' } ]);
    },[createConditionsComponent, suspectConditionList]);

    const chronicConditionComponents = React.useMemo<JSX.Element>(() => {
        return createConditionsComponent(setChronicConditionList, verifyCanAddConditionRow, chronicConditionList,
            'Chronic Conditions', 'Enter the chronic condition and ICD code:', TrackableMeasureTypes.CHRONIC,
            [ { key: 'name', label: 'Name' }, { key: 'value', label: 'ICD' } ]);
    },[createConditionsComponent, chronicConditionList]);

    const diagnosedConditionComponents = React.useMemo<JSX.Element>(() => {
        return createConditionsComponent(setDiagnosedConditionList, verifyCanAddConditionRow, diagnosedConditionList,
            'Diagnosed Conditions', 'Enter the diagnosed condition and ICD code:', TrackableMeasureTypes.DIAGNOSIS,
            [ { key: 'name', label: 'Name' }, { key: 'value', label: 'ICD' } ]);
    },[createConditionsComponent, diagnosedConditionList]);

    const measuresComponents = React.useMemo<JSX.Element>(() => {
        return createConditionsComponent(setMeasureList, verifyCanAddConditionRow, measureList,
            'Measures', 'Select Measure:', TrackableMeasureTypes.MEASURE,
            [
                { key: 'name', label: 'Measure', entries:measureListKeys, type:'enum' },
                { key: 'value', label: 'State', entries:complianceListKeys, type:'enum', default: 'NC' },
            ]);
    },[createConditionsComponent, measureList, measureListKeys, complianceListKeys]);

    const checkFormHasErrors = React.useCallback(() => {
        const hasErrors = Object.values(caseManagerErrors)?.find(e => e)
            || Object.values(demographicErrors)?.find(e => e)
            || Object.values(contactErrors)?.find(e => e)
            || Object.values(clientErrors)?.find(e => e)
            || Object.values(providerErrors)?.find(e => e);
        const hasSubmitter = !submitterFirstName || submitterFirstName.trim().length == 0
            || !submitterLastName || submitterLastName.trim().length == 0
            || (!submitterEmail || !(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/).test(submitterEmail))
        return hasErrors || hasSubmitter || false;
    }, [caseManagerErrors, demographicErrors, contactErrors, clientErrors, providerErrors,
        submitterFirstName, submitterLastName, submitterEmail])

    const submitPatient = async (client:Client, patient:Patient, insurances:Insurance[], visit:Visit, 
        measures:TrackableMeasure[], signer:Signer, documents:DocContainer[]) => {
        publicAPI 
            ? await submitPatientPublic(client, patient, insurances, visit, measures, 
                    signer, documents, (error) => snackBar.showError(new Error(error)))
            : await submitPatientPrivate(client._id.toString(), patient, insurances, visit);
    }

    const onSubmitPatient = async () => {
        const patient = {...demographicsPatient, ...contactsPatient, ...clientsPatient, ...caseManagerPatient, ...providersPatient,
            status:PatientStatus.ACTIVE, clientId:clientId, firstName:demographicsPatient.firstName?.toUpperCase(),
            lastName:demographicsPatient.lastName?.toUpperCase(),
        };
        const notes = ((dischargePlace?.trim()?.length || 0) === 0 ? '' : `Discharged From: ${dischargePlace}\n`) +
            ((dischargeDate?.trim()?.length || 0) === 0 ? '' : `Discharged On: ${dischargeDate}\n`) +
            (dischargeNotes || '');

        const trackableElements:TrackableMeasure[] = [...chronicConditionList, 
            ...diagnosedConditionList, ...suspectConditionList, ...measureList ];

        const now = new Date();
        const nowStr = now.toISOString();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const documents = (files || []).map((file) => { 
            return  {
                src:file.previewUrl,
                file:file.file,
                doc:{
                    patientId:patient._id,
                    clientId:patient.clientId,
                    details:file.description,
                    emr:[{key:'*', asset:'', status:DocumentStatus.PENDING}],
                    created:nowStr,
                    size:file.file.size,
                    type:file.file.type,
                    documentType:file.documentType,
                    name:`${patient.lastName?.toLocaleLowerCase()}_${patient.firstName?.toLocaleLowerCase()}`
                        + `_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
                        + `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
                        + `${file.file.name.substring(file.file.name.lastIndexOf('.'))}`

                } as DocumentRef
            }
        });

        const signerData = {
            firstName:submitterFirstName||'',
            lastName:submitterLastName||'',
            contact:submitterEmail||'',
            contactType:'email' as 'email'|'phone'
        };

        setReferalSubmitOpen(true);
        try {
            const discharge = dischargePlace || dischargeDate ? { place:dischargePlace, date:dischargeDate } : undefined;
            const visit = {
                status:VisitStatus.NEW, visitReasons:visitReasons,
                clientNotes:notes.trim() !== '' ? notes : undefined,
                clientId:clientId, numCalls:0, numEmails:0, numMails:0, numContacts:0, numSMS:0,
                discharge:discharge
            } as Visit;

            const client = clientQuery.data;
            if(!client) {
                throw new Error('Invalid Client');
            }

            await submitPatient(client, patient as Patient, insuranceList, visit,
                trackableElements, signerData, documents
            )
        } catch(error) {
            console.error(error);
            setReferalSubmitOpen(false);
            if((error instanceof Error) && (error as Error)?.message?.includes('Duplicate')) {
                setDiagDuplicate(true);
            } else {
                setDiagFailed(true);
            }
            return;
        }
        setReferalSubmitOpen(false);
        setDiagSuccess(true);
    }

    return (<Paper elevation={6} square sx={{ height: '100vh', display: 'flex', flexDirection: 'column', }}>
        <AppBar position="fixed">
            <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', position: 'relative', }} >
                <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }} >
                <Typography variant="h6" component="div">
                    Patient Referral Form
                </Typography>
                <Typography variant="subtitle1" component="div">
                    { clientQuery?.isLoading ? 'Loading' : clientQuery?.data?.name || ''}
                </Typography>
                </Box>
            </Box>
            </Toolbar>
        </AppBar>
        {
            (!clientQuery || clientQuery?.isLoading || clientQuery.isError) ? 
            <Box sx={{ position: 'fixed', top: 0, left: 0, height: '100vh', width: '100vw',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1300 }} >
                {
                    clientQuery.isError ? <Stack direction={'column'} justifyContent='center' alignItems='center'>
                            <Typography variant='h4'><b>Opps, Page Is Unavailable</b></Typography>
                            <p/>
                            <Typography >An Unexpected Error has occured, please try refreshing the page to continue.</Typography>
                        </Stack>
                        : <CircularProgress />

                }
            </Box>
            :
            <>
            <Toolbar />
            <Box sx={{ px: '10%', pt: '1em', boxShadow:1 }}>
                {demographicsComponents}
            </Box>
            <Box sx={{ overflowY: 'auto', px: '8%', pt: '1em', marginBottom: '56px'}} >
                <Grid container spacing={1.05}>
                    {contactComponents}
                    {clientComponents}
                    {visitReasonComponents}
                    {providerComponents}
                    {caseManagerComponents}
                    <GridFormItem xs={12}>
                        <Box sx={{border:1, borderRadius:1, padding:'1%'}}>
                        <CollapsibleSection header={
                            <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                                Insurance
                            </Typography>
                        }>
                            <InsuranceList patient={demographicsPatient} onUpdate={setInsuranceList}
                                insurance={insuranceList}
                                requestInsuranceProviders={
                                    publicAPI ? async () => {
                                        const items = await PublicAPI.getInsuranceProviders();
                                        return {insurance_providers:{count:items.length, items:items}};
                                } : undefined  }/>
                        </CollapsibleSection>
                        </Box>
                    </GridFormItem>
                    <GridFormItem xs={12}>
                        <Box sx={{border:1, borderRadius:1, padding:'1%'}}>
                        <CollapsibleSection header={
                            <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                                Conditions & Measures
                            </Typography>
                        }>
                            {measuresComponents}
                            <Divider/>
                            {suspectConditionComponents}
                            <Divider/>
                            {chronicConditionComponents}
                            <Divider/>
                            {diagnosedConditionComponents}
                        </CollapsibleSection>
                        </Box>
                    </GridFormItem>
                    
                    <GridFormItem xs={12}>
                        <Box sx={{border:1, borderRadius:1, padding:'1%'}}>
                            <CollapsibleSection header={
                                <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start', alignItems:'center' }}>
                                    Documents
                                </Typography>
                            }>
                                <FileUploadPreview usePublicAPI={publicAPI} onUpdate={setFiles} files={files}/>
                            </CollapsibleSection>
                        </Box>
                    </GridFormItem>
                </Grid>
                <GridFormItem xs={12}><Box height={'1em'}/></GridFormItem>
            </Box>

            <BottomNavigation sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: 'white', borderTop: '1px solid #ddd', }}>
                <Box sx={{  display: 'flex',  justifyContent: 'space-between',  alignItems: 'center',  px: '8%', width: '100%', boxSizing: 'border-box'}}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box component="label" sx={{ minWidth: 100, fontWeight: 'bold' }}>
                        Submitter:
                        </Box>
                        <TextField color="primary"  label='First Name'  value={submitterFirstName} size='small' sx={{width:250}}
                            onChange={(e) => setSubmitterFirstName(e.target.value) }
                            error={(!submitterFirstName || submitterFirstName?.trim().length == 0)}/>
                        <TextField color="primary"  label='Last Name'  value={submitterLastName} size='small' sx={{width:250}}
                            onChange={(e) => setSubmitterLastName(e.target.value) }
                            error={(!submitterLastName || submitterLastName?.trim().length == 0)}/>
                        <TextField color="primary"  label='Email' value={submitterEmail} size='small' sx={{width:300}}
                            onChange={(e) => setSubmitterEmail(e.target.value) }
                            error={(!submitterEmail || !(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/).test(submitterEmail))}/>
                    </Box>

                    <Button variant="contained" color="primary" disabled={checkFormHasErrors()}
                        onClick={() => setConfirmDiag(true)}>
                        Submit
                    </Button>
                </Box>
            </BottomNavigation>
        </>}
        <ReferralSubmitDialog open={referalSubmitOpen} clientName={clientQuery?.data?.name || 'Opps'} 
            patientName={`${demographicsPatient?.lastName}, ${demographicsPatient?.firstName}`}
            submitterName={`${submitterLastName}, ${submitterFirstName}`}
            submitterEmail={submitterEmail || ''}/>
        <Dialog open={confirmDiag} onClose={()=>setConfirmDiag(false)}>
            <DialogTitle align="center">{'Submit Patient'}</DialogTitle>
            <DialogContent>
                <>
                    <Box alignContent="center" alignSelf='center' padding={'4px'}>
                        <Typography variant="body1" align="center"> <b>{`${demographicsPatient?.lastName}, ${demographicsPatient?.firstName}`}</b></Typography>
                        <Typography variant="body1"align="center"> {`Will be submitted to ${clientQuery.data?.name}.`}</Typography>
                        <br/>
                        <Typography variant="body1" align="center"> {`Do you want to continue?`}</Typography>
                    </Box>
                    <br/>
                    <Box alignContent="center" alignSelf='center' border={1} padding={'6px'}>
                        <Typography variant="body2" align="center">
                            {'By clicking "Continue," you acknowledge and agree to allow the Patient Information and the Submitter Name, Contact Information, and IP address to be stored on our backend.'}
                        </Typography>
                        <p/>
                        <Typography variant="body2" align="center">
                            {'If you wish to request the removal of this data, please contact us at '}
                            <Link href="mailto:help@ziphycare.com" underline="hover">help@ziphycare.com</Link>
                        </Typography>
                    </Box>
                </>
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="space-between" width="100%" padding={'6px'}>
                    <Button onClick={() => {setConfirmDiag(false)}}>Close</Button>
                    <Button variant='contained' onClick={() => {
                        setReferalSubmitOpen(true);
                        setConfirmDiag(false);
                        onSubmitPatient()
                    }}>Continue</Button>
                </Box>
            </DialogActions>
        </Dialog>
        <SimpleDialog open={diagSuccess} title={<b>{'Success'}</b>}
            body={<><Typography variant="body1" align="center"> {`Patient was successfully submitted.`}</Typography></>}
            buttons={[
                {text:'Close', onClick:() => {
                    setDiagSuccess(false)
                    window.location.reload();
                 }},
            ]}
        />
        <SimpleDialog open={diagDuplicate} title={<b>{'Error'}</b>}
            body={<><Typography variant="body1" align="center"> {`An unknown error has occured. Please contact office to resolve.`}</Typography></>}
            buttons={[{text:'Close', onClick:()=>setDiagDuplicate(false) }]}
        />
        <SimpleDialog open={diagFailed} title={<b>{'Error'}</b>}
            body={<><Typography variant="body1" align="center"> {`An unknown error has occured. Please try again in a few minutes.`}</Typography></>}
            buttons={[{text:'Close', onClick:()=>setDiagFailed(false) }]}
        />
        <AlertSnack {...snackBar}/>
        </Paper>
    );
}

export default ReferalsPage;